import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { PlIcon } from '@app/icon-library/icons';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { CustomValidatorErrors } from '@app/components/pl-input/email-validator';

export enum Key {
  enter = 'Enter',
  escape = 'Escape',
}

export enum PlInputType {
  text = 'text',
  email = 'email',
  password = 'password',
}

let identifier: number = 0;

@Component({
  selector: 'pl-input',
  templateUrl: './pl-input.component.html',
})
export class PlInputComponent implements OnInit, ControlValueAccessor {
  @Input({ required: true }) public label: string = '';
  @Input() public autocomplete: string = 'off_off_off';
  @Input() public isDisabled: boolean = false;
  @Input() public isFormSubmitted: boolean = false;
  @Input() public maxlength: number | null = 250;
  @Input() public name: string = '';
  @Input() public patternErrorMessage: string | null = null;
  @Input() public invalidatePatternValidationIfEmojiInValue: boolean = false;
  @Input() public placeholder: string = '';
  @Input() public requiredErrorMessage: string | null = null;
  @Input() public showEyeIconToTogglePassword: boolean = false;
  @Input() public showRightIcon: boolean = true;
  @Input() public type: PlInputType = PlInputType.text;

  @Output() public specialKeyPressed: EventEmitter<Key> = new EventEmitter();

  public value: string = '';

  public identifier: string = `form-pl-textbox-${identifier++}`;

  public isFocused: boolean = false;

  private onChangeCallback!: (value: string) => void;
  private onTouchedCallback!: () => void;

  public readonly IconFrame: typeof IconFrame = IconFrame;
  public readonly CustomValidatorErrors: typeof CustomValidatorErrors = CustomValidatorErrors;
  public readonly PlInputType: typeof PlInputType = PlInputType;
  public readonly PlIcon: typeof PlIcon = PlIcon;

  @HostBinding('tabindex') public tabindex: number = 1;
  @HostListener('focus') public onFocus(): void {
    console.log('\x1b[41m TONIO PlInputComponent onFocus =', '\x1b');
    // preventScroll: true because scroll is handled by directive plScrollToInvalidControlOnSubmit
    this.inputElement.nativeElement.focus({ preventScroll: true });
  }

  @ViewChild('inputElement', { read: ElementRef }) public inputElement!: ElementRef;

  constructor(@Optional() @Self() public controlDir: NgControl) {
    if (this.controlDir) {
      this.controlDir.valueAccessor = this;
    }
  }

  public ngOnInit(): void {
    if (this.controlDir) {
      const control: AbstractControl | null = this.controlDir.control;

      if (control) {
        const validators: ValidatorFn[] = control.validator ? [control.validator] : [];
        control.setValidators([this.validate.bind(this, validators)]);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  private validate(validators: ValidatorFn[]): ValidationErrors | null {
    const allErrors: any = {};
    for (const validator of validators) {
      const formControl: UntypedFormControl = new UntypedFormControl(this.value);
      const hasError: ValidationErrors | null = validator(formControl);
      if (hasError) {
        Object.entries(hasError).forEach(([errorName, isOnError]: [string, boolean]) => {
          allErrors[errorName] = isOnError;
        });
      }
    }

    if (this.maxlength !== null && this.value?.length > this.maxlength) {
      allErrors.maxlength = {
        requiredLength: this.maxlength,
        actualLength: this.value.length,
      };
    }

    if (this.invalidatePatternValidationIfEmojiInValue) {
      const emojiRegExp: RegExp =
        /^.*[\u{1f300}-\u{1f5ff}\u{1f900}-\u{1f9ff}\u{1f600}-\u{1f64f}\u{1f680}-\u{1f6ff}\u{2600}-\u{26ff}\u{2700}-\u{27bf}\u{1f1e6}-\u{1f1ff}\u{1f191}-\u{1f251}\u{1f004}\u{1f0cf}\u{1f170}-\u{1f171}\u{1f17e}-\u{1f17f}\u{1f18e}\u{3030}\u{2b50}\u{2b55}\u{2934}-\u{2935}\u{2b05}-\u{2b07}\u{2b1b}-\u{2b1c}\u{3297}\u{3299}\u{303d}\u{00a9}\u{00ae}\u{2122}\u{23f3}\u{24c2}\u{23e9}-\u{23ef}\u{25b6}\u{23f8}-\u{23fa}]+.*/gu;
      if (emojiRegExp.test(this.value)) {
        allErrors.pattern = true;
      }
    }

    if (Object.keys(allErrors).length > 0) {
      return allErrors;
    }

    return null;
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public writeValue(valueToWrite: string): void {
    if (valueToWrite !== undefined) {
      this.value = valueToWrite;
    }
    this.updateValueAndValidity();
  }

  public onCustomInputFocus(): void {
    this.isFocused = true;
  }

  public onBlur(): void {
    this.onTouched();
    this.isFocused = false;
  }

  public onTouched(): void {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  public onChange(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.specialKeyPressed.emit(Key.enter);
    } else if (event.key === 'Escape') {
      this.specialKeyPressed.emit(Key.escape);
    } else {
      const inputElement: HTMLInputElement = event.target as HTMLInputElement;
      this.value = inputElement.value;
      this.controlDir.control?.updateValueAndValidity({ emitEvent: false });
      this.emitChanges();
    }
  }

  private emitChanges(): void {
    this.onChangeCallback(this.value);
  }

  public togglePassword(): void {
    this.type = this.type === PlInputType.text ? PlInputType.password : PlInputType.text;
  }

  protected updateValueAndValidity(): void {
    if (this.controlDir.control) {
      this.controlDir.control.updateValueAndValidity({
        onlySelf: false,
        emitEvent: false,
      });
    }
  }
}
